import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Field as FormikField, Form as FormikForm, useFormikContext } from 'formik'
import styles from './GroupWorkPlaceForm.module.scss'
import { formFieldNames } from './formFieldNames'
import RadioStaff from '@/components/RadioStaff/RadioStaff'
import { Button, Field } from '@/ui'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { handbookSelectors } from '@/entity/handbook'
import { formatDecimalInput } from '@/utils'
import { WrappedCheckbox } from '@features/contracts/registrationEmployerContract/form/fields'
import { getPositionsByEstablishedPost, getStaffCategory } from '@/entity/staffingProject/thunks'
import { staffSelectors } from '@/entity'
import { convertDataToSelectData } from '@/shared'

export const GroupWorkPlaceForm = ({ edit, onClose, actualStaffing, structureDeptTypeCode }) => {
	const dispatch = useDispatch()
	const { t } = useTranslation()
	const { setFieldValue, values } = useFormikContext()
	const positions = useSelector(handbookSelectors.positions)
	const positionsByKnz = useSelector(handbookSelectors.positionsByKnz)
	const laborMobilityCenterCategory = useSelector(handbookSelectors.laborMobilityCenterCategory)
	const dictEstablishedPost = useSelector(staffSelectors.dictEstablishedPost)
	const [localPositionByNkz, setLocalPositionByNkz] = useState(positionsByKnz || [])

	const { positionByDeptType, activeCompany } = useSelector((state) => ({
		positionByDeptType: state.staff.positionByDeptType,
		activeCompany: state.company.company,
	}))

	const [isMsuDisabledStaffCategory, setIsMsuDisabledStaffCategory] = useState(!activeCompany?.msu)

	// Храним выбранный establishedPostCode, чтобы корректно подставлять его при запросе категории
	const establishedPostCodeRef = useRef(null)

	const handleMsuSetValueStaffCategory = useCallback(
		(code) => {
			let find = positionByDeptType.data?.find((i) => i.code === code)
			setIsMsuDisabledStaffCategory(find?.categoryUnlocked)

			if (!values?.dstaffCategory) {
				setFieldValue(formFieldNames.dstaffCategory, find?.category?.code)
			}
		},
		[positionByDeptType.data]
	)

	const handleHealthcareSetValueStaffCategory = useCallback(
		(positionCode, establishedPostCodeOverride) => {
			const establishedPostCode = establishedPostCodeOverride || establishedPostCodeRef.current

			if (!establishedPostCode || !positionCode) {
				return
			}

			const body = {
				data: {
					establishedPostCode,
					positionCode,
				},
				onSuccess: (res) => {
					if (res) {
						setFieldValue(formFieldNames.dstaffCategory, res)
					}
				},
			}
			dispatch(getStaffCategory(body))
		},
		[dispatch, setFieldValue]
	)

	const checkPositions = useMemo(() => {
		if (activeCompany?.sphereType) {
			// Для гражданских служащих используем dictEstablishedPost, уже отфильтрованный и конвертированный
			const filteredPositions = dictEstablishedPost?.data?.filter((item) => item.isLast) || []
			return convertDataToSelectData(filteredPositions)
		}
		// Для остальных используем positions и фильтруем по isLast
		return positions?.filter((position) => position.isLast) || []
	}, [activeCompany?.sphereType, dictEstablishedPost?.data, positions])

	/**
	 * Автозаполнение категории для гражданских служащих при создании группы
	 * Берём значение из deptTypeCode структуры
	 */
	useEffect(() => {
		if (activeCompany?.sphereType && structureDeptTypeCode && !values?.dstaffCategory && laborMobilityCenterCategory) {
			// Проверяем, что такой код существует в справочнике
			const categoryExists = laborMobilityCenterCategory.find((item) => item.code === structureDeptTypeCode)
			if (categoryExists) {
				setFieldValue(formFieldNames.dstaffCategory, structureDeptTypeCode)
			}
		}
	}, [
		activeCompany?.sphereType,
		edit,
		structureDeptTypeCode,
		setFieldValue,
		laborMobilityCenterCategory,
		values?.dstaffCategory,
	])

	useEffect(() => {
		if (activeCompany?.msu) {
			setLocalPositionByNkz(positionByDeptType.data || [])
		}
	}, [positionByDeptType.data])

	useEffect(() => {
		if (positionByDeptType?.data && (activeCompany?.msu || activeCompany?.sphereType)) {
			handleMsuSetValueStaffCategory(values?.dposition)
		}
	}, [positionByDeptType?.data])

	const filteredLaborMobilityCenterCategory = useMemo(() => {
		if (activeCompany?.msu) {
			return laborMobilityCenterCategory?.filter((i) => i.id !== 5)
		} else {
			return laborMobilityCenterCategory
		}

	}, [laborMobilityCenterCategory, activeCompany?.msu])

	const handleSphereSetValueEstablishedPost = useCallback(
		(code) => {
			// Сохраняем выбранный establishedPostCode
			establishedPostCodeRef.current = code

			setFieldValue(formFieldNames.dposition, null)

			let body = {
				data: code,
				onSuccess: (res) => {
					let filtered = positionsByKnz.filter((i) => {
						return res.includes(i.code)
					})

					setLocalPositionByNkz(filtered)

					// Автоселект при единственном варианте для гражданских служащих
					if (filtered && filtered.length === 1) {
						const autoCode = filtered[0]?.code
						if (autoCode) {
							setFieldValue(formFieldNames.dposition, autoCode)

							// Для healthcare автоматически получаем категорию
							if (activeCompany?.sphereType === 'healthcare') {
								handleHealthcareSetValueStaffCategory(autoCode, code)
							}
						}
					} else if (activeCompany?.sphereType && edit && filtered && filtered.length > 1) {
						// При редактировании групп и наличии нескольких вариантов выбираем первый по умолчанию,
						// при этом поле остаётся активным для изменения
						const firstCode = filtered[0]?.code
						if (firstCode) {
							setFieldValue(formFieldNames.dposition, firstCode)

							// Для healthcare автоматически получаем категорию
							if (activeCompany?.sphereType === 'healthcare') {
								handleHealthcareSetValueStaffCategory(firstCode, code)
							}
						}
					}
				},
			}
			dispatch(getPositionsByEstablishedPost(body))
		},
		[positionByDeptType.data, activeCompany?.sphereType, handleHealthcareSetValueStaffCategory]
	)

	return (
		<FormikForm className={styles.form}>
			<section>
				<div className={styles.item}>
					<div className={`${styles.checkboxFields} ${styles.fields_verticalCenter}`}>
						<div className={styles.label}>
							<FormikField name={formFieldNames.freelance}>
								{({ field }) => (
									<WrappedCheckbox
										{...field}
										label={t('staff_modal.freelancer_position')}
										disabled={edit && !actualStaffing}
									/>
								)}
							</FormikField>
						</div>
					</div>
					<FormikField name={formFieldNames.isDicPosition}>
						{({ field }) => (
							<div className={'flex'}>
								<RadioStaff
									label={t('workplace.manual_input')}
									value="2"
									id="2"
									onChange={(e) => setFieldValue(field.name, e.target.value)}
									checked={field.value === '2'}
									disabled={activeCompany?.sphereType}
								/>
								<RadioStaff
									label={t('workplace.directory')}
									value="3"
									id="3"
									onChange={(e) => setFieldValue(field.name, e.target.value)}
									checked={field.value === '3'}
									disabled={activeCompany?.sphereType}
								/>
							</div>
						)}
					</FormikField>
					{values[formFieldNames.isDicPosition] === '2' ? (
						<div className={styles.twoFields}>
							<FormikField name={formFieldNames.nameKz}>
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										type="text"
										name={t('staff_modal.position_kz')}
										label={t('staff_modal.position_kz')}
										placeholder={t('staff_modal.position_placeholder')}
										isRequired
										error={touched && error}
									/>
								)}
							</FormikField>
							<FormikField name={formFieldNames.nameRu}>
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										type="text"
										label={t('staff_modal.position_ru')}
										placeholder={t('staff_modal.position_placeholder')}
										isRequired
										error={touched && error}
									/>
								)}
							</FormikField>
						</div>
					) : (
						<FormikField name="destablishedPost">
							{({ field, meta: { touched, error } }) => (
								<Field
									{...field}
									fieldType="selectSearch"
									label={t('contract.info.position')}
									hint={t('hint_position')}
									placeholder={t('position_placeholder')}
									options={checkPositions}
									error={touched && error}
									isRequired
									onChange={(value) => {
										// Явно сохраняем значение в Formik и в ref
										setFieldValue(field.name, value)
										establishedPostCodeRef.current = value

										if (activeCompany?.sphereType && value) {
											if (edit && dictEstablishedPost?.data) {
												const positionInEstablishedPost = dictEstablishedPost.data.find(
													(item) => item.code === value && item.isLast
												)

												if (!positionInEstablishedPost) {
													// Код не совпадает с dictEstablishedPost - очищаем поля
													setFieldValue(formFieldNames.destablishedPost, '')
													setFieldValue(formFieldNames.dposition, null)
													return
												}
											}

											handleSphereSetValueEstablishedPost(value)
										}

										if (activeCompany?.msu) {
											setFieldValue(formFieldNames.dposition, null)
											setFieldValue(formFieldNames.dstaffCategory, null)
										}
									}}
								/>
							)}
						</FormikField>
					)}
					<FormikField name={formFieldNames.dposition}>
						{({ field, meta: { touched, error } }) => {
							let disabled = activeCompany?.sphereType ? true : activeCompany?.msu ? positionByDeptType.loading : false
							return (
								<Field
									{...field}
									fieldType="selectSearch"
									label={t('contract.info.nkz_position')}
									hint={t('hint_position_knz')}
									placeholder={t('choose_from_directory')}
									options={localPositionByNkz}
									error={touched && error}
									disabled={disabled}
									isRequired
									onChange={(value) => {
										field.onChange(value)

										if (activeCompany?.msu) {
											handleMsuSetValueStaffCategory(value)
										}

										if (activeCompany?.sphereType === 'healthcare') {
											handleHealthcareSetValueStaffCategory(value, establishedPostCodeRef.current)
										}
									}}
								/>
							)
						}}
					</FormikField>
					<FormikField name={formFieldNames.dstaffCategory}>
						{({ field, meta: { touched, error } }) => {
							let checkMsu = isMsuDisabledStaffCategory ? isMsuDisabledStaffCategory : false
							let checkSphere = true
							let disabled = activeCompany?.msu ? checkMsu : activeCompany?.sphereType ? checkSphere : false
							return (
								<Field
									{...field}
									fieldType="select"
									label={t('staff_modal.position_category')}
									placeholder={t('choose_from_directory')}
									options={filteredLaborMobilityCenterCategory}
									error={touched && error}
									isRequired
									disabled={disabled}
								/>
							)
						}}
					</FormikField>
					{!edit ? (
						<div className={`${styles.fields} ${styles.threeFields}`}>
							<FormikField name={formFieldNames.workplaceCount}>
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										type="number"
										hideNumberArrows
										placeholder={t('staff.staff_units_placeholder')}
										error={touched && error}
										label={t('workplace.tariff_rate')}
										disabled={edit}
										isRequired={!edit}
									/>
								)}
							</FormikField>
							<FormikField name={formFieldNames.grossSalary}>
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										type="text"
										hideNumberArrows
										fieldType={'inputNumber'}
										label={t('staffing_table.salary')}
										placeholder={t('staff_modal.salary_placeholder')}
										error={touched && error}
										value={field.value}
										onChange={(event) => {
											const next = formatDecimalInput(event.target.value, 2)
											field.onChange(next)
										}}
										autoComplete="off"
										disabled={edit}
										isRequired={!edit}
									/>
								)}
							</FormikField>
							<FormikField name={formFieldNames.salarySupplement}>
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										type="text"
										fieldType={'inputNumber'}
										hideNumberArrows
										label={t('staffing_table.supplement_surcharge')}
										placeholder={t('staff_modal.supplement_surcharge_placeholder')}
										error={touched && error}
										value={field.value}
										onChange={(event) => {
											const next = formatDecimalInput(event.target.value, 2)
											field.onChange(next)
										}}
										autoComplete="off"
										disabled={edit}
									/>
								)}
							</FormikField>
						</div>
					) : null}
					{edit ? null : (
						<p>
							{t('workplace.count_tariff_rate')}
							<i>{' - ' + t('staff.staff_units_count_hint')}</i>
						</p>
					)}
				</div>
			</section>
			<div className={styles.actions}>
				<Button type="bare" onClick={onClose}>
					{t('cancel')}
				</Button>
				<Button textColor="green" borderColor="green" transparent border type="submit">
					{t('save')}
				</Button>
			</div>
		</FormikForm>
	)
}
